import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, map } from 'rxjs/operators';
import { isNumber } from 'highcharts';

import { BurnListService, SiteTwinService, SiteTypeService } from 'src/api/services';
import { BurnList, BurnListRegion, BurnListStatus, SiteTwin } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { BurnListDataService } from 'src/service/burn-list-data.service';
import { ToastrService } from 'ngx-toastr';
import { AzureAuthService } from 'src/service/azure-auth.service';

@Component({
  selector: 'app-fire-foresight-burn-list-overview',
  templateUrl: './fire-foresight-burn-list-overview.component.html',
  styleUrls: ['./fire-foresight-burn-list-overview.component.css']
})
export class FireForesightBurnListOverviewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public loading: boolean = false;
  public burnLists: BurnList[] = [];
  public filteredBurnLists: BurnList[] = [];
  public burnListStatuses: BurnListStatus[] = [];
  public burnListRegions: BurnListRegion[] = [];
  public burnListTypes: BurnListStatus[] = []; // Using BurnListStatus as the type is reused
  public selectedRegionId: number | null = null;
  public selectedRegion: BurnListRegion | null = null;
  public selectedTypeId: number | null = null;
  public selectedType: BurnListStatus | null = null;

  // Refresh interval (in milliseconds) - 30 seconds for TV display
  private refreshInterval: any;
  private readonly REFRESH_RATE = 30000;

  // Site twins for burn coups fuel moisture data
  public burnCoupSiteTwins: Map<number, SiteTwin> = new Map();
  public siteTwinsLoading: boolean = false;

  // Fuel moisture refresh and change tracking
  private fuelMoistureRefreshInterval: any = null;
  private readonly FUEL_MOISTURE_REFRESH_MS = 2 * 60 * 1000; // 2 minutes
  private previousFuelMoistureValues: Map<string, { shaded: string | null, unshaded: string | null }> = new Map();
  public updatedFuelMoistureValues: Map<string, { shaded: boolean, unshaded: boolean }> = new Map();

  // EPA Air Quality site twins
  public airQualitySites: any[] = [];
  public airQualitySitesLoading: boolean = false;
  private airQualityRefreshInterval: any = null;
  private readonly AIR_QUALITY_REFRESH_MS = 5 * 60 * 1000; // 5 minutes
  private previousAirQualityValues: Map<string, { pm10: number | null, pm25: number | null }> = new Map();
  public updatedAirQualityValues: Map<string, { pm10: boolean, pm25: boolean }> = new Map();

  // Weather Station sites
  public weatherStationSites: any[] = [];
  public weatherStationSitesLoading: boolean = false;
  private weatherStationRefreshInterval: any = null;
  private readonly WEATHER_STATION_REFRESH_MS = 5 * 60 * 1000; // 5 minutes
  private previousWeatherStationValues: Map<string, { temp: number | null, humidity: number | null, windSpeed: number | null }> = new Map();
  public updatedWeatherStationValues: Map<string, { temp: boolean, humidity: boolean, windSpeed: boolean }> = new Map();

  constructor(
    private route: ActivatedRoute,
    private burnListService: BurnListService,
    private siteTwinService: SiteTwinService,
    private siteTypeService: SiteTypeService,
    public app: ApplicationConfigService,
    private toasterService: ToastrService,
    public azureAuthService: AzureAuthService,
    private burnListDataService: BurnListDataService
  ) {}

  ngOnInit(): void {
    console.log("FireForesightBurnListOverviewComponent initialized");

    // Subscribe to route parameters with distinctUntilChanged to prevent duplicate loads
    this.route.params
      .pipe(
        map(params => ({
          regionId: params['burnListRegionId'] ? parseInt(params['burnListRegionId']) : null,
          typeId: params['burnListTypeId'] ? parseInt(params['burnListTypeId']) : null
        })),
        distinctUntilChanged((prev, curr) => 
          prev.regionId === curr.regionId && prev.typeId === curr.typeId
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ regionId, typeId }) => {
        this.selectedRegionId = regionId;
        this.selectedTypeId = typeId;
        this.loadData();
      });

    // Set up auto-refresh for TV display
    this.refreshInterval = setInterval(() => {
        this.loadData(true); // Silent refresh
    }, this.REFRESH_RATE);

    // Set up fuel moisture refresh timer
    this.startFuelMoistureRefreshTimer();

    // Set up air quality refresh timer
   this.startAirQualityRefreshTimer();

   // Set up weather station refresh timer
   this.startWeatherStationRefreshTimer();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    if (this.fuelMoistureRefreshInterval) {
      clearInterval(this.fuelMoistureRefreshInterval);
      this.fuelMoistureRefreshInterval = null;
    }

    if (this.airQualityRefreshInterval) {
      clearInterval(this.airQualityRefreshInterval);
      this.airQualityRefreshInterval = null;
    }

    if (this.weatherStationRefreshInterval) {
      clearInterval(this.weatherStationRefreshInterval);
      this.weatherStationRefreshInterval = null;
    }
  }

  loadData(silent: boolean = false): void {
    // Always set loading to true to prevent empty state from showing
    this.loading = true;

    // make sure this.app.customerId is available before loading data
    if (this.app.customerId == null) {
        this.app.customerId = this.azureAuthService.userProfile.customerId;
      }

    // Load all data in parallel
    Promise.all([
      this.loadBurnLists(),
      this.loadBurnListStatuses(),
      this.loadBurnListRegions(),
      this.loadBurnListTypes()
    ]).then(() => {
      this.filterBurnLists();
      // Load fuel moisture data after burn lists are loaded
      this.loadFuelMoistureForAllBurnLists();
      // Load air quality data
      this.loadAirQualitySites();
      // Load weather station data
      this.loadWeatherStationSites();
      // Always set loading to false when complete
      this.loading = false;
    }).catch(error => {
      console.error('Error loading burn list overview data:', error);
      if (!silent) {
        this.toasterService.error('Failed to load burn list data');
      }
      // Always set loading to false on error
      this.loading = false;
    });
  }

  async loadBurnLists(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.burnListDataService.getBurnLists(this.app.customerId).subscribe(
        (data) => {
          this.burnLists = data || [];
          resolve();
        },
        (error) => {
          console.error('Error loading burn lists:', error);
          reject(error);
        }
      );
    });
  }

  async loadBurnListStatuses(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.burnListDataService.getBurnListStatuses().subscribe(
        (statuses) => {
          this.burnListStatuses = statuses || [];
          resolve();
        },
        (error) => {
          console.error('Error loading burn list statuses:', error);
          reject(error);
        }
      );
    });
  }

  async loadBurnListRegions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.burnListDataService.getBurnListRegions().subscribe(
        (regions) => {
          this.burnListRegions = regions || [];
          if (this.selectedRegionId) {
            this.selectedRegion = this.burnListRegions.find(r => r.id === this.selectedRegionId) || null;
          }
          resolve();
        },
        (error) => {
          console.error('Error loading burn list regions:', error);
          reject(error);
        }
      );
    });
  }

  async loadBurnListTypes(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.burnListDataService.getBurnListTypes().subscribe(
        (types) => {
          this.burnListTypes = types || [];
          if (this.selectedTypeId) {
            this.selectedType = this.burnListTypes.find(t => t.id === this.selectedTypeId) || null;
          }
          resolve();
        },
        (error) => {
          console.error('Error loading burn list types:', error);
          reject(error);
        }
      );
    });
  }

  filterBurnLists(): void {
    let filtered = [...this.burnLists];

    // Filter by region if selected
    if (this.selectedRegionId) {
      filtered = filtered.filter(bl => bl.burnListRegionId === this.selectedRegionId);
    }

    // Filter by type if selected
    if (this.selectedTypeId) {
      filtered = filtered.filter(bl => bl.burnListTypeId === this.selectedTypeId);
    }

    // Only show planned or active burn lists (hide completed and draft)
    filtered = filtered.filter(bl => {
      const statusName = this.getBurnListStatusName(bl.burnListStatusId).toLowerCase();
      return statusName.includes('planned') || 
             statusName.includes('scheduled') || 
             statusName.includes('progress') || 
             statusName.includes('active') ||
             statusName.includes('start'); // Include "started" status
    });

    this.filteredBurnLists = filtered;

    // Update selected region and type objects
    this.selectedRegion = this.selectedRegionId 
      ? this.burnListRegions.find(r => r.id === this.selectedRegionId) || null 
      : null;
    
    this.selectedType = this.selectedTypeId 
      ? this.burnListTypes.find(t => t.id === this.selectedTypeId) || null 
      : null;
  }

  getBurnListStatusName(burnListStatusId: number): string {
    const status = this.burnListStatuses.find(s => s.id === burnListStatusId);
    return status ? status.name : 'Unknown';
  }

  getBurnListStatusColor(burnListStatusId: number): string {
    const statusName = this.getBurnListStatusName(burnListStatusId).toLowerCase();

    if (statusName.includes('completed') || statusName.includes('finished')) {
      return 'bg-gradient-success';
    } else if (statusName.includes('progress') || statusName.includes('active')) {
      return 'bg-gradient-warning';
    } else if (statusName.includes('planned') || statusName.includes('scheduled')) {
      return 'bg-gradient-info';
    } else if (statusName.includes('cancelled') || statusName.includes('abandoned')) {
      return 'bg-gradient-danger';
    }

    return 'bg-gradient-secondary';
  }

  getEnabledCoupsCount(burnList: BurnList): number {
    if (!burnList.burnCoups) return 0;
    return burnList.burnCoups.filter(coup => !coup.disabled).length;
  }

  getTotalCoupsCount(burnList: BurnList): number {
    return burnList.burnCoups?.length || 0;
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Fuel Moisture Methods

  loadFuelMoistureForAllBurnLists(): void {
    if (this.filteredBurnLists.length === 0) {
      console.log('No burn lists to load fuel moisture for');
      return;
    }

    // Collect all burn coups from all filtered burn lists
    const allBurnCoups: any[] = [];
    this.filteredBurnLists.forEach(burn => {
      if (burn.burnCoups && burn.burnCoups.length > 0) {
        allBurnCoups.push(...burn.burnCoups);
      }
    });

    if (allBurnCoups.length === 0) {
      console.log('No burn coups in burn lists');
      return;
    }

    // Only show loading indicator if we don't have any data yet (initial load)
    const isInitialLoad = this.burnCoupSiteTwins.size === 0;
    if (isInitialLoad) {
      this.siteTwinsLoading = true;
    }

    const siteTypeCode = 'Fuel Moisture';

    console.log(`Loading site twins for ${allBurnCoups.length} burn coups using bulk search...`);

    // Use bulk search to get all sites at once
    this.siteTypeService.SiteTypeSearch({
      customerId: this.app.customerId,
      type: siteTypeCode,
      searchSize: 1000,
      pageIndex: 0,
      filterOld: false
    }).subscribe(
      (sites) => {
        if (!sites || sites.length === 0) {
          console.log('No sites returned from bulk search');
          this.siteTwinsLoading = false;
          this.detectFuelMoistureChanges();
          return;
        }

        console.log(`Bulk search returned ${sites.length} sites`);

        // Create a map of sites by siteUri for quick lookup
        const sitesMap = new Map<string, any>();
        sites.forEach(site => {
          if (site.siteUri) {
            sitesMap.set(site.siteUri.toLowerCase(), site);
          }
        });

        // Match sites to burn coups and store in burnCoupSiteTwins
        let matchedCount = 0;
        allBurnCoups.forEach(burnCoup => {
          if (!burnCoup.geoAsset || !burnCoup.geoAsset.identifier) {
            return;
          }

          const siteTwinUri = burnCoup.geoAsset.identifier.toLowerCase();
          const site = sitesMap.get(siteTwinUri);

          if (site && site.parameters) {
            // Convert site to SiteTwin format and store
            this.burnCoupSiteTwins.set(burnCoup.id, site as SiteTwin);
            matchedCount++;
          }
        });

        this.siteTwinsLoading = false;
        console.log(`Matched ${matchedCount} site twins to burn coups. Total in map: ${this.burnCoupSiteTwins.size}`);
        this.detectFuelMoistureChanges();
      },
      (error) => {
        console.error('Failed to load site twins via bulk search:', error);
        this.siteTwinsLoading = false;
        this.detectFuelMoistureChanges();
      }
    );
  }

  getEnabledCoupsForBurn(burn: BurnList): any[] {
    if (!burn.burnCoups) return [];
    return burn.burnCoups.filter(coup => !coup.disabled);
  }

  getFuelMoistureValue(burnCoupId: number, shade?: boolean): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }

    const keys = Object.keys(siteTwin.parameters);
    const fuelMoistureKeys = keys.filter(k =>
      k.toLowerCase().includes('fuel') && k.toLowerCase().includes('moisture')
    );

    if (fuelMoistureKeys.length === 0) {
      return null;
    }

    if (shade === undefined) {
      const parameter = siteTwin.parameters[fuelMoistureKeys[0]];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        return null;
      }
      return parameter.latestValue.value;
    }

    for (const key of fuelMoistureKeys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData['shade'] && p.metaData["unitType"] == "Fuel Moisture") {
        const isShaded = p.metaData['shade'].toLowerCase() === 'true';
        if (isShaded === shade) {
          return p.latestValue.value;
        }
      }
    }

    return null;
  }

  getFuelMoistureShadedValue(burnCoupId: number): string | null {
    return this.getFuelMoistureValue(burnCoupId, true);
  }

  getFuelMoistureUnshadedValue(burnCoupId: number): string | null {
    return this.getFuelMoistureValue(burnCoupId, false);
  }

  /**
   * Check if fuel moisture data is older than 6 hours
   */
  isFuelMoistureDataStale(burnCoupId: number, shade: boolean): boolean {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return false;
    }

    const keys = Object.keys(siteTwin.parameters);
    const fuelMoistureKeys = keys.filter(k =>
      k.toLowerCase().includes('fuel') && k.toLowerCase().includes('moisture')
    );

    for (const key of fuelMoistureKeys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData['shade'] && p.metaData["unitType"] == "Fuel Moisture") {
        const isShaded = p.metaData['shade'].toLowerCase() === 'true';
        if (isShaded === shade && p.latestValue.timeStamp) {
          const dataTime = new Date(p.latestValue.timeStamp);
          const now = new Date();
          const sixHoursInMs = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
          const timeDiff = now.getTime() - dataTime.getTime();
          return timeDiff > sixHoursInMs;
        }
      }
    }

    return false;
  }

  /**
   * Get badge class for fuel moisture value, returns bg-light if data is stale (>6 hours old)
   */
  getFuelMoistureBadgeClassWithStaleCheck(burnCoupId: number, value: string | null, shade: boolean = false): string {
    if (!value) return 'bg-secondary';

    // Check if data is stale (older than 6 hours)
    if (this.isFuelMoistureDataStale(burnCoupId, shade)) {
      return 'bg-light';
    }

    return this.getFuelMoistureBadgeClass(value, shade);
  }

  getFuelMoistureBadgeClass(value: string | null, shade: boolean = false): string {
    if (!value) return 'bg-secondary';

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'bg-secondary';

    if (shade) {
      if (numValue < 17) return 'bg-danger';
      return 'bg-success';
    } else {
      if (numValue < 9) return 'bg-danger';
      if (numValue < 14) return 'bg-success';
      if (numValue < 16) return 'bg-warning';
      return 'bg-danger';
    }
  }

  /**
   * Get SDI (Soil Dryness Index) value for a burn coup
   */
  getSDIValue(burnCoupId: number): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }

    const keys = Object.keys(siteTwin.parameters);
    
    for (const key of keys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData["unitType"] === "SDI") {
        return Math.round(p.latestValue.value).toString();
      }
    }

    return null;
  }

  /**
   * Get SDI timestamp for a burn coup
   */
  getSDITimestamp(burnCoupId: number): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }

    const keys = Object.keys(siteTwin.parameters);
    
    for (const key of keys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.timeStamp) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData["unitType"] === "SDI") {
        return p.latestValue.timeStamp;
      }
    }

    return null;
  }

  /**
   * Get the age of SDI data in milliseconds
   */
  getSDIAge(burnCoupId: number): number | null {
    const timestamp = this.getSDITimestamp(burnCoupId);
    
    if (!timestamp) {
      return null;
    }

    const now = new Date();
    const time = new Date(timestamp);
    return now.getTime() - time.getTime();
  }

  /**
   * Check if SDI data is older than 40 hours
   */
  isSDIDataStale(burnCoupId: number): boolean {
    const timestamp = this.getSDITimestamp(burnCoupId);
    if (!timestamp) {
      return false;
    }
    
    const dataTime = new Date(timestamp);
    const now = new Date();
    const fortyHoursInMs = 40 * 60 * 60 * 1000; // 40 hours in milliseconds
    const timeDiff = now.getTime() - dataTime.getTime();
    return timeDiff > fortyHoursInMs;
  }

  /**
   * Get badge class for SDI value
   */
  getSDIBadgeClass(value: string | null): string {
    if (!value) return 'bg-secondary';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'bg-secondary';

    // Color coding based on SDI value
    // Higher SDI = higher fire risk
    if (numValue > 100) return 'bg-danger';      // Over 100 - dangerous
    return 'bg-success';                          // Under/equal 100 - safe
  }

  /**
   * Get badge class for SDI value, returns bg-light if data is stale (>6 hours old)
   */
  getSDIBadgeClassWithStaleCheck(burnCoupId: number, value: string | null): string {
    if (!value) return 'bg-secondary';
    
    // Check if data is stale (older than 6 hours)
    if (this.isSDIDataStale(burnCoupId)) {
      return 'bg-light';
    }
    
    return this.getSDIBadgeClass(value);
  }

  getBurnPlanOverallFuelMoistureColor(burn: BurnList): string {
    const enabledCoups = this.getEnabledCoupsForBurn(burn);
    if (enabledCoups.length === 0) {
      return 'bg-secondary';
    }

    let hasRed = false;
    let hasOrange = false;
    let hasGreen = false;
    let hasData = false;

    enabledCoups.forEach(coup => {
      const shadedValue = this.getFuelMoistureShadedValue(coup.id);
      const unshadedValue = this.getFuelMoistureUnshadedValue(coup.id);

      // Check shaded value (skip if stale)
      if (shadedValue !== null && !this.isFuelMoistureDataStale(coup.id, true)) {
        hasData = true;
        const shadedClass = this.getFuelMoistureBadgeClass(shadedValue, true);
        if (shadedClass === 'bg-danger') hasRed = true;
        else if (shadedClass === 'bg-warning') hasOrange = true;
        else if (shadedClass === 'bg-success') hasGreen = true;
      }

      // Check unshaded value (skip if stale)
      if (unshadedValue !== null && !this.isFuelMoistureDataStale(coup.id, false)) {
        hasData = true;
        const unshadedClass = this.getFuelMoistureBadgeClass(unshadedValue, false);
        if (unshadedClass === 'bg-danger') hasRed = true;
        else if (unshadedClass === 'bg-warning') hasOrange = true;
        else if (unshadedClass === 'bg-success') hasGreen = true;
      }
    });

    if (hasRed) return 'bg-danger';
    if (hasOrange) return 'bg-warning';
    if (hasGreen) return 'bg-success';

    return 'bg-secondary';
  }

  startFuelMoistureRefreshTimer(): void {
    console.log('Starting fuel moisture refresh timer (2 minutes)');
    this.fuelMoistureRefreshInterval = setInterval(() => {
      this.refreshFuelMoistureData();
    }, this.FUEL_MOISTURE_REFRESH_MS);
  }

  refreshFuelMoistureData(): void {
    console.log('Refreshing fuel moisture data...');
    this.storePreviousFuelMoistureValues();
    this.loadFuelMoistureForAllBurnLists();
  }

  storePreviousFuelMoistureValues(): void {
    this.previousFuelMoistureValues.clear();

    const burnCoups: any[] = [];
    this.filteredBurnLists.forEach(burn => {
      if (burn.burnCoups) {
        burnCoups.push(...burn.burnCoups);
      }
    });

    burnCoups.forEach(coup => {
      const key = `${coup.id}`;
      const shadedValue = this.getFuelMoistureShadedValue(coup.id);
      const unshadedValue = this.getFuelMoistureUnshadedValue(coup.id);
      this.previousFuelMoistureValues.set(key, { shaded: shadedValue, unshaded: unshadedValue });
    });
  }

  detectFuelMoistureChanges(): void {
    this.updatedFuelMoistureValues.clear();

    this.previousFuelMoistureValues.forEach((prevValues, coupId) => {
      const burnCoupId = parseInt(coupId);
      const currentShaded = this.getFuelMoistureShadedValue(burnCoupId);
      const currentUnshaded = this.getFuelMoistureUnshadedValue(burnCoupId);

      const shadedChanged = prevValues.shaded !== currentShaded;
      const unshadedChanged = prevValues.unshaded !== currentUnshaded;

      if (shadedChanged || unshadedChanged) {
        this.updatedFuelMoistureValues.set(coupId, {
          shaded: shadedChanged,
          unshaded: unshadedChanged
        });

        setTimeout(() => {
          this.updatedFuelMoistureValues.delete(coupId);
        }, 1000);
      }
    });

    if (this.updatedFuelMoistureValues.size > 0) {
      console.log('Detected fuel moisture changes:', this.updatedFuelMoistureValues);
    }
  }

  hasFuelMoistureUpdated(burnCoupId: number, type: 'shaded' | 'unshaded'): boolean {
    const key = `${burnCoupId}`;
    const update = this.updatedFuelMoistureValues.get(key);
    if (!update) return false;
    return type === 'shaded' ? update.shaded : update.unshaded;
  }

  // EPA Air Quality Methods

  startAirQualityRefreshTimer(): void {
    // Load immediately
    this.loadAirQualitySites();

    // Then refresh every 5 minutes
    this.airQualityRefreshInterval = setInterval(() => {
      this.loadAirQualitySites();
    }, this.AIR_QUALITY_REFRESH_MS);
  }

  loadAirQualitySites(): void {
    if (!this.app.customerId) {
      console.warn('No customer ID available for loading air quality sites');
      return;
    }

    // Store previous values before loading new data
    this.storePreviousAirQualityValues();

    // Only show loading indicator if we don't have any data yet (initial load)
    const isInitialLoad = this.airQualitySites.length === 0;
    if (isInitialLoad) {
      this.airQualitySitesLoading = true;
    }

    const siteTypeCode = 'EPA Air Quality';

    this.siteTypeService.SiteTypeSearch({
      customerId: this.app.customerId,
      type: siteTypeCode,
      searchSize: 1000,
      pageIndex: 0,
      filterOld: false
    }).subscribe(
      (sites) => {
        // Update existing sites in-place to preserve object references for change detection
        const newSites = sites || [];

        // Create a map of existing sites by URI for quick lookup
        const existingSitesMap = new Map();
        this.airQualitySites.forEach(site => {
          const key = site.siteUri || site.siteName;
          if (key) existingSitesMap.set(key, site);
        });

        // Update or add sites
        const updatedSites = newSites.map(newSite => {
          const key = newSite.siteUri || newSite.siteName;
          const existingSite = existingSitesMap.get(key);

          if (existingSite) {
            // Update existing site properties in-place
            Object.assign(existingSite, newSite);
            return existingSite;
          } else {
            // New site
            return newSite;
          }
        });

        this.airQualitySites = updatedSites;
        this.airQualitySitesLoading = false;
        console.log(`Loaded ${this.airQualitySites.length} EPA Air Quality sites`, this.airQualitySites);
        this.detectAirQualityChanges();
      },
      (error) => {
        console.error('Failed to load EPA Air Quality sites', error);
        this.airQualitySitesLoading = false;
      }
    );
  }

  getPM10Value(site: any): number | null {
    if (!site.parameters) {
      return null;
    }

    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'PM10') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }

    return null;
  }

  getPM25Value(site: any): number | null {
    if (!site.parameters) {
      return null;
    }

    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'PM25') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }

    return null;
  }

  getPM25Color(pm25Value: number | null): string {
    if (pm25Value === null || pm25Value === undefined) {
      return 'bg-secondary'; // Grey for no data
    }

    if (pm25Value < 10) {
      return 'bg-success'; // Green (matches Fuel Moisture green)
    } else {
      return 'bg-danger'; // Red (matches Fuel Moisture red)
    }
  }

  getPM10Color(pm10Value: number | null): string {
    if (pm10Value === null || pm10Value === undefined) {
      return 'bg-secondary'; // Grey for no data
    }

    if (pm10Value < 50) {
      return 'bg-success'; // Green (matches Fuel Moisture green)
    } else {
      return 'bg-danger'; // Red (matches Fuel Moisture red)
    }
  }

  // Legacy method for backwards compatibility
  getAirQualityColor(pmValue: number | null): string {
    return this.getPM10Color(pmValue);
  }

  getAirQualityLevel(pmValue: number | null): string {
    if (pmValue === null || pmValue === undefined) {
      return 'Unknown';
    }

    if (pmValue < 10) {
      return 'Good';
    } else if (pmValue < 25) {
      return 'Fairly Good';
    } else if (pmValue < 50) {
      return 'Fairly Poor';
    } else if (pmValue < 100) {
      return 'Poor';
    } else if (pmValue < 300) {
      return 'Very Poor';
    } else {
      return 'Extremely Poor';
    }
  }

  // Distance and filtering methods

  /**
   * Calculate distance between two lat/lon points in kilometers using Haversine formula
   */
  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Radius of Earth in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  }

  toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Calculate centroid from GeoJSON geometry
   */
  getCentroidFromGeoJson(geoJson: string): { lat: number, lon: number } | null {
    try {
      // Parse the GeoJSON if it's a string, otherwise use as-is
      const parsed = typeof geoJson === 'string' ? JSON.parse(geoJson) : geoJson;

      if (!parsed) {
        console.warn('getCentroidFromGeoJson: No parsed GeoJSON');
        return null;
      }

      // Extract the actual geometry object
      // Could be a Feature (with geometry property) or raw Geometry (with type and coordinates)
      let geometry: any;
      if (parsed.type === 'Feature' && parsed.geometry) {
        geometry = parsed.geometry;
      } else if (parsed.type === 'FeatureCollection' && parsed.features && parsed.features.length > 0) {
        // For FeatureCollection, use the first feature's geometry
        geometry = parsed.features[0].geometry;
      } else if (parsed.coordinates) {
        // Raw geometry object
        geometry = parsed;
      } else {
        console.warn('getCentroidFromGeoJson: Unrecognized GeoJSON structure', parsed);
        return null;
      }

      if (!geometry || !geometry.coordinates) {
        console.warn('getCentroidFromGeoJson: No coordinates found in geometry', geometry);
        return null;
      }

      let allCoords: number[][] = [];

      // Handle different GeoJSON geometry types
      if (geometry.type === 'Polygon') {
        // Polygon coordinates are [[[lon, lat], [lon, lat], ...]]
        allCoords = geometry.coordinates[0];
      } else if (geometry.type === 'MultiPolygon') {
        // MultiPolygon coordinates are [[[[lon, lat], ...]], [[[lon, lat], ...]]]
        geometry.coordinates.forEach((polygon: any) => {
          allCoords = allCoords.concat(polygon[0]);
        });
      } else if (geometry.type === 'Point') {
        // Point is just [lon, lat]
        return { lat: geometry.coordinates[1], lon: geometry.coordinates[0] };
      } else if (geometry.type === 'LineString') {
        // LineString is [[lon, lat], [lon, lat], ...]
        allCoords = geometry.coordinates;
      } else if (geometry.type === 'MultiLineString') {
        // MultiLineString is [[[lon, lat], ...], [[lon, lat], ...]]
        geometry.coordinates.forEach((line: any) => {
          allCoords = allCoords.concat(line);
        });
      } else {
        console.warn('getCentroidFromGeoJson: Unsupported geometry type', geometry.type);
        return null;
      }

      if (allCoords.length === 0) {
        console.warn('getCentroidFromGeoJson: No coordinates extracted');
        return null;
      }

      // Calculate average lat/lon
      let sumLat = 0;
      let sumLon = 0;
      allCoords.forEach((coord: number[]) => {
        sumLon += coord[0];
        sumLat += coord[1];
      });

      return {
        lat: sumLat / allCoords.length,
        lon: sumLon / allCoords.length
      };
    } catch (error) {
      console.error('Error parsing GeoJSON:', error, geoJson);
      return null;
    }
  }

  /**
   * Calculate the centroid of all burn coups in a burn list
   */
  getBurnListCentroid(burnList: BurnList): { lat: number, lon: number } | null {
    const enabledCoups = this.getEnabledCoupsForBurn(burnList);
    console.log('getBurnListCentroid called', {
      hasBurnList: !!burnList,
      totalCoups: burnList?.burnCoups?.length || 0,
      enabledCoups: enabledCoups.length
    });

    if (enabledCoups.length === 0) {
      console.warn('No enabled coups in burn list');
      return null;
    }

    const centroids: { lat: number, lon: number }[] = [];

    enabledCoups.forEach((coup, index) => {
      if (coup.geoAsset && coup.geoAsset.geometryGeoJson) {
        console.log(`Processing coup ${index + 1}/${enabledCoups.length}: ${coup.geoAsset.identifier}`);
        const centroid = this.getCentroidFromGeoJson(coup.geoAsset.geometryGeoJson);
        if (centroid) {
          console.log(`Centroid found for coup ${coup.geoAsset.identifier}:`, centroid);
          centroids.push(centroid);
        } else {
          console.warn(`Failed to get centroid for coup ${coup.geoAsset.identifier}`);
        }
      } else {
        console.warn(`Coup ${index + 1} missing geoAsset or geometryGeoJson:`, coup);
      }
    });

    if (centroids.length === 0) {
      console.error('No valid centroids found from coup geometries. Checked', enabledCoups.length, 'coups');
      return null;
    }

    // Calculate average of all coup centroids
    let sumLat = 0;
    let sumLon = 0;
    centroids.forEach(c => {
      sumLat += c.lat;
      sumLon += c.lon;
    });

    const result = {
      lat: sumLat / centroids.length,
      lon: sumLon / centroids.length
    };
    console.log(`Calculated burn list centroid from ${centroids.length} coups:`, result);
    return result;
  }

  /**
   * Get air quality sites within 20km of burn list centroid (with distance)
   */
  getNearbyAirQualitySites(burnList: BurnList): any[] {
    const centroid = this.getBurnListCentroid(burnList);

    if (!centroid) {
      return [];
    }

    const maxDistance = 20; // kilometers

    // Map all sites with distance
    const sitesWithDistance = this.airQualitySites
      .filter(site => site.latitude && site.longitude)
      .map(site => ({
        ...site,
        distance: this.calculateDistance(
          centroid.lat,
          centroid.lon,
          site.latitude,
          site.longitude
        )
      }));

    // Filter sites within 20km
    const sitesWithin20km = sitesWithDistance.filter(site => site.distance <= maxDistance);

    // If we have sites within 20km, return them
    if (sitesWithin20km.length > 0) {
      return sitesWithin20km;
    }

    // Otherwise, return the nearest site (even if beyond 20km)
    const nearestSite = sitesWithDistance.length > 0
      ? sitesWithDistance.sort((a, b) => a.distance - b.distance)[0]
      : null;
    return nearestSite ? [nearestSite] : [];
  }

  getNearestAirQualitySite(burnList: BurnList): any & { distance: number } | null {
    const centroid = this.getBurnListCentroid(burnList);

    if (!centroid) {
      return null;
    }

    if (this.airQualitySites.length === 0) {
      return null;
    }

    const sitesWithDistance = this.airQualitySites
      .filter(site => site.latitude && site.longitude)
      .map(site => ({
        ...site,
        distance: this.calculateDistance(
          centroid.lat,
          centroid.lon,
          site.latitude,
          site.longitude
        )
      }))
      .sort((a, b) => a.distance - b.distance); // Sort by distance

    return sitesWithDistance.length > 0 ? sitesWithDistance[0] : null;
  }

  hasAirQualitySitesWithin20km(burnList: BurnList): boolean {
    const centroid = this.getBurnListCentroid(burnList);

    if (!centroid) {
      return false;
    }

    const maxDistance = 20; // kilometers

    const sitesWithin20km = this.airQualitySites.filter(site => {
      if (!site.latitude || !site.longitude) {
        return false;
      }

      const distance = this.calculateDistance(
        centroid.lat,
        centroid.lon,
        site.latitude,
        site.longitude
      );

      return distance <= maxDistance;
    });

    return sitesWithin20km.length > 0;
  }

  getNearestAirQualityDistance(burnList: BurnList): number | null {
    const nearestSite = this.getNearestAirQualitySite(burnList);
    return nearestSite ? nearestSite.distance : null;
  }

  // Weather Station Methods

  startWeatherStationRefreshTimer(): void {
    // Load immediately
    this.loadWeatherStationSites();

    // Then refresh every 5 minutes
    this.weatherStationRefreshInterval = setInterval(() => {
      this.loadWeatherStationSites();
    }, this.WEATHER_STATION_REFRESH_MS);
  }

  loadWeatherStationSites(): void {
    if (!this.app.customerId) {
      console.warn('No customer ID available for loading weather stations');
      return;
    }

    // Store previous values before loading new data
    this.storePreviousWeatherStationValues();

    // Only show loading indicator if we don't have any data yet (initial load)
    const isInitialLoad = this.weatherStationSites.length === 0;
    if (isInitialLoad) {
      this.weatherStationSitesLoading = true;
    }

    const siteTypeCode = 'Weather Station';

    this.siteTypeService.SiteTypeSearch({
      customerId: this.app.customerId,
      type: siteTypeCode,
      searchSize: 1000,
      pageIndex: 0,
      filterOld: false
    }).subscribe(
      (sites) => {
        // Update existing sites in-place to preserve object references for change detection
        const newSites = sites || [];

        // Create a map of existing sites by URI for quick lookup
        const existingSitesMap = new Map();
        this.weatherStationSites.forEach(site => {
          const key = site.siteUri || site.siteName;
          if (key) existingSitesMap.set(key, site);
        });

        // Update or add sites
        const updatedSites = newSites.map(newSite => {
          const key = newSite.siteUri || newSite.siteName;
          const existingSite = existingSitesMap.get(key);

          if (existingSite) {
            // Update existing site properties in-place
            Object.assign(existingSite, newSite);
            return existingSite;
          } else {
            // New site
            return newSite;
          }
        });

        this.weatherStationSites = updatedSites;
        this.weatherStationSitesLoading = false;
        console.log(`Loaded ${this.weatherStationSites.length} Weather Station sites`, this.weatherStationSites);
        this.detectWeatherStationChanges();
      },
      (error) => {
        console.error('Failed to load Weather Station sites', error);
        this.weatherStationSitesLoading = false;
      }
    );
  }

  getNearestWeatherStation(burnList: BurnList): any & { distance: number } | null {
    const centroid = this.getBurnListCentroid(burnList);

    if (!centroid) {
      return null;
    }

    if (this.weatherStationSites.length === 0) {
      return null;
    }

    const sitesWithDistance = this.weatherStationSites
      .filter(site => site.latitude && site.longitude)
      .map(site => ({
        ...site,
        distance: this.calculateDistance(
          centroid.lat,
          centroid.lon,
          site.latitude,
          site.longitude
        )
      }))
      .sort((a, b) => a.distance - b.distance); // Sort by distance

    return sitesWithDistance.length > 0 ? sitesWithDistance[0] : null;
  }

  getTemperatureValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }

    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'Air Temperature') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }

    return null;
  }

  getHumidityValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }

    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && (parameter.unitType === 'Relative Humidity' || parameter.unitType === 'Humidity')) {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }

    return null;
  }

  getWindSpeedValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }

    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'Wind Speed') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }

    return null;
  }

  getWindDirectionValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }

    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'Wind Direction') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }

    return null;
  }

  getWindData(site: any): { speed: number, direction: number, temperature: number | null, humidity: number | null, age: number } {
    const onehour = 3600000 * 100;
    let speed = 0;
    let direction = 0;
    let temperature: number | null = null;
    let humidity: number | null = null;

    const time = new Date(site.latestTimeStamp);
    const now = new Date();
    const age = now.getTime() - time.getTime();

    if (site.parameters) {
      for (const key in site.parameters) {
        const param = site.parameters[key];

        if (param.unitType === 'Wind Speed' && param.latestValue) {
          const s = Number(param.latestValue.value);
          if (isNumber(s)) {
            speed = s;
          }
        }

        if (param.unitType === 'Wind Direction' && param.latestValue) {
          const d = Number(param.latestValue.value);
          if (isNumber(d) && d >= 0 && d <= 360) {
            direction = d;
          }
        }

        if (param.unitType === 'Air Temperature' && param.latestValue) {
          const t = Number(param.latestValue.value);
          if (isNumber(t)) {
            temperature = t;
          }
        }

        if ((param.unitType === 'Relative Humidity' || param.unitType === 'Humidity') && param.latestValue) {
          const h = Number(param.latestValue.value);
          if (isNumber(h)) {
            humidity = h;
          }
        }
      }
    }

    return { speed, direction, temperature, humidity, age };
  }

  /**
   * Format age in milliseconds to human-readable string
   */
  formatAge(ageMs: number | null): string {
    if (ageMs === null || ageMs === undefined) {
      return 'N/A';
    }

    const minutes = Math.floor(ageMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else {
      return `${minutes}m`;
    }
  }

  // Air Quality change detection
  storePreviousAirQualityValues(): void {
    this.previousAirQualityValues.clear();

    this.filteredBurnLists.forEach(burnList => {
      const nearbySites = this.getNearbyAirQualitySites(burnList);
      nearbySites.forEach(site => {
        const key = `${burnList.id}-${site.siteUri || site.siteName}`;
        const pm10 = this.getPM10Value(site);
        const pm25 = this.getPM25Value(site);
        this.previousAirQualityValues.set(key, { pm10, pm25 });
      });
    });
  }

  detectAirQualityChanges(): void {
    this.updatedAirQualityValues.clear();

    this.filteredBurnLists.forEach(burnList => {
      const nearbySites = this.getNearbyAirQualitySites(burnList);
      nearbySites.forEach(site => {
        const key = `${burnList.id}-${site.siteUri || site.siteName}`;
        const prevValues = this.previousAirQualityValues.get(key);

        if (prevValues) {
          const currentPm10 = this.getPM10Value(site);
          const currentPm25 = this.getPM25Value(site);

          const pm10Changed = prevValues.pm10 !== currentPm10;
          const pm25Changed = prevValues.pm25 !== currentPm25;

          if (pm10Changed || pm25Changed) {
            this.updatedAirQualityValues.set(key, { pm10: pm10Changed, pm25: pm25Changed });

            setTimeout(() => {
              this.updatedAirQualityValues.delete(key);
            }, 800);
          }
        }
      });
    });
  }

  hasAirQualityUpdated(burnListId: number, site: any, type: 'pm10' | 'pm25'): boolean {
    const key = `${burnListId}-${site.siteUri || site.siteName}`;
    const update = this.updatedAirQualityValues.get(key);
    if (!update) return false;
    return type === 'pm10' ? update.pm10 : update.pm25;
  }

  // Weather Station change detection
  storePreviousWeatherStationValues(): void {
    this.previousWeatherStationValues.clear();

    this.filteredBurnLists.forEach(burnList => {
      const station = this.getNearestWeatherStation(burnList);
      if (station) {
        const key = `${burnList.id}-${station.siteUri || station.siteName}`;
        const temp = this.getTemperatureValue(station);
        const humidity = this.getHumidityValue(station);
        const windSpeed = this.getWindSpeedValue(station);
        this.previousWeatherStationValues.set(key, { temp, humidity, windSpeed });
      }
    });
  }

  detectWeatherStationChanges(): void {
    this.updatedWeatherStationValues.clear();

    this.filteredBurnLists.forEach(burnList => {
      const station = this.getNearestWeatherStation(burnList);
      if (station) {
        const key = `${burnList.id}-${station.siteUri || station.siteName}`;
        const prevValues = this.previousWeatherStationValues.get(key);

        if (prevValues) {
          const currentTemp = this.getTemperatureValue(station);
          const currentHumidity = this.getHumidityValue(station);
          const currentWindSpeed = this.getWindSpeedValue(station);

          const tempChanged = prevValues.temp !== currentTemp;
          const humidityChanged = prevValues.humidity !== currentHumidity;
          const windSpeedChanged = prevValues.windSpeed !== currentWindSpeed;

          if (tempChanged || humidityChanged || windSpeedChanged) {
            this.updatedWeatherStationValues.set(key, {
              temp: tempChanged,
              humidity: humidityChanged,
              windSpeed: windSpeedChanged
            });

            setTimeout(() => {
              this.updatedWeatherStationValues.delete(key);
            }, 800);
          }
        }
      }
    });
  }

  hasWeatherStationUpdated(burnListId: number, station: any, type: 'temp' | 'humidity' | 'windSpeed'): boolean {
    const key = `${burnListId}-${station.siteUri || station.siteName}`;
    const update = this.updatedWeatherStationValues.get(key);
    if (!update) return false;
    if (type === 'temp') return update.temp;
    if (type === 'humidity') return update.humidity;
    if (type === 'windSpeed') return update.windSpeed;
    return false;
  }

  /**
   * TrackBy function for Air Quality sites in *ngFor
   * Helps Angular identify items so it only updates changed values
   */
  trackAirQualitySite(index: number, site: any): string {
    return site.siteUri || site.siteName || index.toString();
  }

  // Air Quality methods for individual burn coups

  /**
   * Get the centroid of a specific burn coup
   */
  getBurnCoupCentroid(burnCoup: any): { lat: number, lon: number } | null {
    if (!burnCoup || !burnCoup.geoAsset || !burnCoup.geoAsset.geometryGeoJson) {
      return null;
    }
    return this.getCentroidFromGeoJson(burnCoup.geoAsset.geometryGeoJson);
  }

  /**
   * Find the closest air quality site to a specific burn coup
   */
  getClosestAirQualitySiteForBurnCoup(burnCoup: any): any & { distance: number } | null {
    const centroid = this.getBurnCoupCentroid(burnCoup);
    
    if (!centroid) {
      return null;
    }
    
    if (this.airQualitySites.length === 0) {
      return null;
    }

    const sitesWithDistance = this.airQualitySites
      .filter(site => site.latitude && site.longitude)
      .map(site => ({
        ...site,
        distance: this.calculateDistance(
          centroid.lat,
          centroid.lon,
          site.latitude,
          site.longitude
        )
      }))
      .sort((a, b) => a.distance - b.distance);

    return sitesWithDistance.length > 0 ? sitesWithDistance[0] : null;
  }

  /**
   * Get PM10 value for a specific burn coup (from closest air quality station)
   */
  getPM10ValueForBurnCoup(burnCoupId: number): number | null {
    // Find the burn coup in all burn lists
    let burnCoup: any = null;
    for (const burnList of this.filteredBurnLists) {
      if (burnList.burnCoups) {
        burnCoup = burnList.burnCoups.find(c => c.id === burnCoupId);
        if (burnCoup) break;
      }
    }

    if (!burnCoup) {
      return null;
    }

    const closestSite = this.getClosestAirQualitySiteForBurnCoup(burnCoup);
    if (!closestSite) {
      return null;
    }

    return this.getPM10Value(closestSite);
  }

  /**
   * Get PM2.5 value for a specific burn coup (from closest air quality station)
   */
  getPM25ValueForBurnCoup(burnCoupId: number): number | null {
    // Find the burn coup in all burn lists
    let burnCoup: any = null;
    for (const burnList of this.filteredBurnLists) {
      if (burnList.burnCoups) {
        burnCoup = burnList.burnCoups.find(c => c.id === burnCoupId);
        if (burnCoup) break;
      }
    }

    if (!burnCoup) {
      return null;
    }

    const closestSite = this.getClosestAirQualitySiteForBurnCoup(burnCoup);
    if (!closestSite) {
      return null;
    }

    return this.getPM25Value(closestSite);
  }

  /**
   * Get color class for PM10 value for a burn coup
   */
  getPM10ColorForBurnCoup(burnCoupId: number): string {
    const value = this.getPM10ValueForBurnCoup(burnCoupId);
    return this.getPM10Color(value);
  }

  /**
   * Get color class for PM2.5 value for a burn coup
   */
  getPM25ColorForBurnCoup(burnCoupId: number): string {
    const value = this.getPM25ValueForBurnCoup(burnCoupId);
    return this.getPM25Color(value);
  }

  /**
   * Get the distance to the closest air quality station for a burn coup
   */
  getClosestAirQualityStationDistanceForBurnCoup(burnCoupId: number): number | null {
    // Find the burn coup in all burn lists
    let burnCoup: any = null;
    for (const burnList of this.filteredBurnLists) {
      if (burnList.burnCoups) {
        burnCoup = burnList.burnCoups.find(c => c.id === burnCoupId);
        if (burnCoup) break;
      }
    }

    if (!burnCoup) {
      return null;
    }

    const closestSite = this.getClosestAirQualitySiteForBurnCoup(burnCoup);
    return closestSite ? closestSite.distance : null;
  }

  /**
   * Get the name of the closest air quality station for a burn coup
   */
  getClosestAirQualityStationNameForBurnCoup(burnCoupId: number): string | null {
    // Find the burn coup in all burn lists
    let burnCoup: any = null;
    for (const burnList of this.filteredBurnLists) {
      if (burnList.burnCoups) {
        burnCoup = burnList.burnCoups.find(c => c.id === burnCoupId);
        if (burnCoup) break;
      }
    }

    if (!burnCoup) {
      return null;
    }

    const closestSite = this.getClosestAirQualitySiteForBurnCoup(burnCoup);
    return closestSite ? (closestSite.siteName || closestSite.name || 'Unknown Station') : null;
  }
}
